import React, { useState, useEffect } from 'react';
import { Card, Button, List, Typography, Space, Tag, message, Alert, Empty } from 'antd';
import {
  FileTextOutlined,
  LinkOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePatientPortal } from '../context/PatientPortalContext';
import moment from 'moment';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const PatientPortalSurveyLinks = () => {
  const { t } = useTranslation();
  const { patient } = usePatientPortal();
  const [surveyLinks, setSurveyLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (patient?._id) {
      fetchSurveyLinks();
    }
  }, [patient]);

  const fetchSurveyLinks = async () => {
    try {
      setLoading(true);
      // Note: This would need to be a public endpoint or use patient portal authentication
      // For now, we'll simulate the data structure
      
      // In a real implementation, you might:
      // 1. Have a separate patient portal API endpoint
      // 2. Use patient portal authentication tokens
      // 3. Or embed survey links in the patient portal context
      
      // Simulated data for demonstration
      const mockSurveyLinks = [
        {
          id: '1',
          token: 'sample-token-123',
          url: `${window.location.origin}/survey/sample-token-123`,
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
          expiresAt: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000), // 28 days from now
          isActive: true,
          isCompleted: false,
          settings: {
            language: 'vi'
          }
        }
      ];
      
      setSurveyLinks(mockSurveyLinks);
    } catch (error) {
      console.error('Error fetching survey links:', error);
      message.error('Failed to load survey links');
    } finally {
      setLoading(false);
    }
  };

  const handleSurveyClick = (surveyLink) => {
    // Open survey in new tab/window
    window.open(surveyLink.url, '_blank', 'noopener,noreferrer');
  };

  const getStatusTag = (link) => {
    if (link.isCompleted) return <Tag color="green" icon={<CheckCircleOutlined />}>Completed</Tag>;
    if (moment(link.expiresAt).isBefore(moment())) return <Tag color="red">Expired</Tag>;
    return <Tag color="blue" icon={<ClockCircleOutlined />}>Pending</Tag>;
  };

  const activeSurveyLinks = surveyLinks.filter(link => 
    link.isActive && 
    moment(link.expiresAt).isAfter(moment()) && 
    !link.isCompleted
  );

  const completedSurveyLinks = surveyLinks.filter(link => link.isCompleted);

  if (loading) {
    return (
      <Card loading={true}>
        <div style={{ height: 200 }} />
      </Card>
    );
  }

  return (
    <div>
      {/* Active Survey Links */}
      {activeSurveyLinks.length > 0 && (
        <Card
          title={
            <Space>
              <FileTextOutlined />
              <span>{t('patientPortal.surveyLinks.pendingSurveys')}</span>
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          <Alert
            message={t('patientPortal.surveyLinks.pendingMessage')}
            description={t('patientPortal.surveyLinks.pendingDescription')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <List
            dataSource={activeSurveyLinks}
            renderItem={(link) => (
              <List.Item
                actions={[
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={() => handleSurveyClick(link)}
                  >
                    {t('patientPortal.surveyLinks.takeSurvey')}
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={<FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                  title={
                    <Space>
                      <span>{t('patientPortal.surveyLinks.healthSurvey')}</span>
                      {getStatusTag(link)}
                    </Space>
                  }
                  description={
                    <div>
                      <Paragraph style={{ margin: 0 }}>
                        <Text type="secondary">
                          {t('patientPortal.surveyLinks.createdOn')}: {moment(link.createdAt).format('MMMM DD, YYYY')}
                        </Text>
                      </Paragraph>
                      <Paragraph style={{ margin: 0 }}>
                        <Text type="secondary">
                          {t('patientPortal.surveyLinks.expiresOn')}: {moment(link.expiresAt).format('MMMM DD, YYYY')}
                        </Text>
                      </Paragraph>
                      <Paragraph style={{ margin: '8px 0 0 0' }}>
                        <Text>
                          {t('patientPortal.surveyLinks.surveyDescription')}
                        </Text>
                      </Paragraph>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* Completed Survey Links */}
      {completedSurveyLinks.length > 0 && (
        <Card
          title={
            <Space>
              <CheckCircleOutlined />
              <span>{t('patientPortal.surveyLinks.completedSurveys')}</span>
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          <List
            dataSource={completedSurveyLinks}
            renderItem={(link) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />}
                  title={
                    <Space>
                      <span>{t('patientPortal.surveyLinks.healthSurvey')}</span>
                      {getStatusTag(link)}
                    </Space>
                  }
                  description={
                    <div>
                      <Paragraph style={{ margin: 0 }}>
                        <Text type="secondary">
                          {t('patientPortal.surveyLinks.completedOn')}: {moment(link.completedAt).format('MMMM DD, YYYY')}
                        </Text>
                      </Paragraph>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* No Survey Links */}
      {surveyLinks.length === 0 && (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Paragraph>{t('patientPortal.surveyLinks.noSurveys')}</Paragraph>
                <Text type="secondary">
                  {t('patientPortal.surveyLinks.noSurveysDescription')}
                </Text>
              </div>
            }
          />
        </Card>
      )}
    </div>
  );
};

export default PatientPortalSurveyLinks;
